/**
 * dateUtils – Single Source of Truth für Datums-Umwandlung im Planer (TIM-32).
 *
 * Kanon: Ein Datum ist im Modell/Transport IMMER ein ISO-String "yyyy-mm-dd"
 * (deckt sich 1:1 mit Rails `t.date`). `Date`-Objekte entstehen nur transient
 * für die D3-Zeitachsen und liegen dann IMMER auf UTC-Mitternacht; sie werden
 * ausschließlich mit getUTC*-Gettern gelesen (D3 nutzt `scaleUtc`). Das deutsche
 * Format "dd-mm-yyyy" ist reine Anzeige.
 *
 * Faustregel: rechnen/vergleichen auf ISO-Strings (lexikografisch = chronologisch,
 * keine Zeitzone im Spiel); `Date` nur an der D3-Grenze über fromIso/toIso.
 */

/** ISO-Kalenderdatum "yyyy-mm-dd" (ohne Uhrzeit, ohne Zeitzone). */
export type IsoDate = string;

/** "yyyy-mm-dd" -> Date auf UTC-Mitternacht (für D3 / scaleUtc). */
export function fromIso(iso: IsoDate): Date {
  return new Date(iso + "T00:00:00Z");
}

/** Date -> "yyyy-mm-dd", gelesen über getUTC* (passend zu fromIso / scaleUtc). */
export function toIso(date: Date): IsoDate {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/**
 * Date -> "yyyy-mm-dd", gelesen über die LOKALEN Felder.
 *
 * Seit TIM-36 nur noch für EINEN Fall: das heutige Kalenderdatum aus der
 * Systemuhr, also `toIsoLocal(new Date())`. `new Date()` ist ein Zeitpunkt, kein
 * Kalendertag — mit `toIso` gelesen läge in Deutschland alles vor 02:00 Uhr auf
 * dem Vortag.
 *
 * Für Datumswerte aus dem Zeitachsen-Raster ist die Funktion NICHT mehr
 * zuständig: dessen Domain liegt jetzt auf UTC-Mitternacht (`fromIso`) und wird
 * mit `d3.utcDay` unterteilt, passend zu `scaleUtc`. Dort gilt `toIso`.
 */
export function toIsoLocal(date: Date): IsoDate {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

/** ISO-Datum um n Tage verschieben (n darf negativ sein). */
export function addDays(iso: IsoDate, n: number): IsoDate {
  const d = fromIso(iso);
  d.setUTCDate(d.getUTCDate() + n);
  return toIso(d);
}

/** Ganzzahlige Tagesdifferenz b - a (positiv, wenn b nach a liegt). */
export function daysBetween(a: IsoDate, b: IsoDate): number {
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  return Math.round((fromIso(b).getTime() - fromIso(a).getTime()) / MS_PER_DAY);
}

/** "yyyy-mm-dd" -> "dd-mm-yyyy" (Anzeige). Leerer String bei ungültigem Input. */
export function toGerman(iso: IsoDate, sep = "-"): string {
  const [year, month, day] = (iso ?? "").split("-");
  if (!year || !month || !day) return "";
  return `${day.padStart(2, "0")}${sep}${month.padStart(2, "0")}${sep}${year}`;
}

/** "dd-mm-yyyy" -> "yyyy-mm-dd". Leerer String bei ungültigem Input. */
export function fromGerman(german: string): IsoDate {
  const [day, month, year] = german.split("-");
  if (!day || !month || !year) return "";
  return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}
